import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

interface ScheduleJob {
  id: string;
  cron: string;
  category: string;
  template?: string | null;
  style?: string | null;
  topic_override?: string | null;
  as_carousel?: boolean;
  slides?: number;
  dry_run: boolean;
  created_at: string;
}

async function listJobs(): Promise<ScheduleJob[]> {
  const r = await fetch("/api/schedule");
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function createJob(body: Omit<ScheduleJob, "id" | "created_at">) {
  const r = await fetch("/api/schedule", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}
async function deleteJob(id: string) {
  const r = await fetch(`/api/schedule/${id}`, { method: "DELETE" });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// ─── Cron picker helpers ─────────────────────────────────────
// Cron field order: minute hour dayOfMonth month dayOfWeek
// Day-of-week: 0=Sunday, 1=Monday, … 6=Saturday.

const DAYS: { short: string; label: string; cron: number }[] = [
  { short: "Mo", label: "Montag", cron: 1 },
  { short: "Di", label: "Dienstag", cron: 2 },
  { short: "Mi", label: "Mittwoch", cron: 3 },
  { short: "Do", label: "Donnerstag", cron: 4 },
  { short: "Fr", label: "Freitag", cron: 5 },
  { short: "Sa", label: "Samstag", cron: 6 },
  { short: "So", label: "Sonntag", cron: 0 },
];

function buildCron(daysOfWeek: number[], hhmm: string): string {
  const [h, m] = hhmm.split(":").map((s) => Number.parseInt(s, 10) || 0);
  const dow = daysOfWeek.length === 0 || daysOfWeek.length === 7
    ? "*"
    : [...daysOfWeek].sort((a, b) => a - b).join(",");
  return `${m} ${h} * * ${dow}`;
}

function summariseCron(cron: string): string {
  // Best-effort humanisation. Falls back to the raw string on anything exotic.
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [m, h, dom, mon, dow] = parts;
  if (dom !== "*" || mon !== "*") return cron;
  if (!/^\d+$/.test(m) || !/^\d+$/.test(h)) return cron;
  const time = `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  if (dow === "*") return `Täglich um ${time}`;
  const dayList = dow.split(",").map((s) => Number.parseInt(s, 10));
  if (!dayList.every((n) => !Number.isNaN(n))) return cron;
  const labels = dayList
    .map((n) => DAYS.find((d) => d.cron === n)?.short ?? String(n))
    .join(", ");
  return `${labels} um ${time}`;
}

// ─── Page ────────────────────────────────────────────────────

export function Schedule() {
  const qc = useQueryClient();
  const { data: jobs = [] } = useQuery({ queryKey: ["schedule"], queryFn: listJobs });

  // Default: weekdays at 08:00 (matches brand_config.yaml default cadence).
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 2, 3, 5]);
  const [time, setTime] = useState("08:00");
  const [category, setCategory] = useState("auto");
  const [template, setTemplate] = useState("");
  const [asCarousel, setAsCarousel] = useState(false);
  const [slides, setSlides] = useState<number>(5);
  const [topic, setTopic] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [rawCron, setRawCron] = useState("");

  // Carousel toggle only valid on non-reel, non-auto categories.
  const carouselToggleAvailable =
    category !== "reel" && category !== "auto" && category !== "carousel";

  const computedCron = useMemo(
    () => (advanced && rawCron ? rawCron : buildCron(selectedDays, time)),
    [advanced, rawCron, selectedDays, time],
  );

  const create = useMutation({
    mutationFn: createJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });
  const remove = useMutation({
    mutationFn: deleteJob,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedule"] }),
  });

  const toggleDay = (cronVal: number) =>
    setSelectedDays((prev) =>
      prev.includes(cronVal)
        ? prev.filter((d) => d !== cronVal)
        : [...prev, cronVal],
    );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!computedCron) return;
    create.mutate({
      cron: computedCron,
      category,
      template: template || undefined,
      topic_override: topic || undefined,
      as_carousel: carouselToggleAvailable && asCarousel ? true : undefined,
      slides: (category === "carousel" || (carouselToggleAvailable && asCarousel)) ? slides : undefined,
      dry_run: false,
    });
    setTopic("");
  };

  return (
    <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
      <div className="xl:col-span-3">
        <h1 className="headline text-6xl text-brand-text-dark">Plan</h1>
        <p className="mt-2 text-sm text-brand-text-dark/60">
          Cron-getriggerte Pipeline-Läufe. Der{" "}
          <code className="rounded bg-black/5 px-1.5">
            python main.py schedule
          </code>{" "}
          Daemon liest{" "}
          <code className="rounded bg-black/5 px-1.5">
            storage/schedule.json
          </code>{" "}
          im 30-Sekunden-Takt und feuert die Jobs auf ihren Cron-Plan.
        </p>

        <div className="mt-8 space-y-3">
          {jobs.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-brand-text-dark/15 bg-white/40 p-8 text-center text-sm text-brand-text-dark/60">
              Keine geplanten Jobs.
            </div>
          ) : (
            jobs.map((j) => (
              <div
                key={j.id}
                className="flex items-center justify-between rounded-xl bg-white px-4 py-3 shadow-sm ring-1 ring-black/5"
              >
                <div className="flex-1">
                  <div className="flex flex-wrap items-baseline gap-3">
                    <span className="text-sm font-bold">
                      {j.category}
                      {j.template ? ` / ${j.template}` : ""}
                    </span>
                    <span className="text-xs text-brand-text-dark/70">
                      {summariseCron(j.cron)}
                    </span>
                    <code className="rounded bg-brand-text-dark/90 px-2 py-0.5 font-mono text-[10px] text-brand-cream">
                      {j.cron}
                    </code>
                  </div>
                  {j.topic_override ? (
                    <div className="mt-1 text-xs text-brand-text-dark/60">
                      Topic: {j.topic_override}
                    </div>
                  ) : null}
                </div>
                <button
                  onClick={() => remove.mutate(j.id)}
                  className="rounded-full px-3 py-1 text-xs text-red-700 hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="xl:col-span-2">
        <h2 className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-brand-text-dark/50">
          Neuer Job
        </h2>
        <form
          onSubmit={submit}
          className="space-y-4 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5"
        >
          <Field label="Wochentage">
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => {
                const on = selectedDays.includes(d.cron);
                return (
                  <button
                    key={d.cron}
                    type="button"
                    onClick={() => toggleDay(d.cron)}
                    className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition ${
                      on
                        ? "bg-brand-blue text-brand-cream"
                        : "bg-brand-beige text-brand-text-dark/70 hover:bg-brand-beige-deep"
                    }`}
                    title={d.label}
                  >
                    {d.short}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-brand-text-dark/50">
              Keine gewählt = jeden Tag.
            </p>
          </Field>

          <Field label="Uhrzeit">
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 font-mono text-sm"
            />
          </Field>

          <Field label="Kategorie">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            >
              {[
                { value: "auto", label: "Auto — Algorithmus wählt" },
                { value: "facts", label: "Facts — Datenpost" },
                { value: "general", label: "General — Brand-Message" },
                { value: "activation", label: "Activation — CTA + URL" },
                { value: "intro", label: "Intro — Tagline" },
                { value: "ending", label: "Ending — Abschluss" },
                { value: "reel", label: "Reel — Hochformat-Video" },
              ].map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>

          {category === "reel" && (
            <Field label="Reel-Template">
              <select
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
              >
                <option value="">Auto — passend zum Topic</option>
                {[
                  { value: "fact_punch", label: "Fact Punch · 9s, nur Typografie" },
                  { value: "myth_vs_fact", label: "Mythos vs Fakt · 15s, narriert" },
                  { value: "claim_crack", label: "Claim Crack · 12s, mit B-Roll" },
                  { value: "testimonial_reel", label: "Testimonial · 15s, Porträt" },
                  { value: "breaking_pattern", label: "Breaking Pattern · 9s, rapid-fire" },
                ].map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </Field>
          )}

          {category === "carousel" && (
            <Field label="Slides">
              <input
                type="number"
                min={3}
                max={10}
                value={slides}
                onChange={(e) => setSlides(Number(e.target.value))}
                className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
              />
            </Field>
          )}

          {carouselToggleAvailable && (
            <div className="rounded-lg bg-brand-beige p-3">
              <label className="flex cursor-pointer items-start gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={asCarousel}
                  onChange={(e) => setAsCarousel(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5"
                />
                <div className="flex-1">
                  <div className="font-bold text-brand-text-dark">
                    Als Carousel rendern
                  </div>
                  <div className="text-brand-text-dark/60">
                    Narrativer Bogen auf <code>{category}</code> getrimmt.
                  </div>
                </div>
              </label>
              {asCarousel && (
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-brand-text-dark/70">
                    Slides
                  </label>
                  <input
                    type="number"
                    min={3}
                    max={10}
                    value={slides}
                    onChange={(e) => setSlides(Number(e.target.value))}
                    className="w-16 rounded-md border border-black/10 bg-white px-2 py-1 text-xs"
                  />
                </div>
              )}
            </div>
          )}

          <Field label="Topic override (optional)">
            <input
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm"
            />
          </Field>

          <div className="rounded-lg bg-brand-beige px-3 py-2 text-xs text-brand-text-dark/70">
            <div className="font-bold uppercase tracking-wider">
              Resultat
            </div>
            <div className="mt-1">{summariseCron(computedCron) || "—"}</div>
            <code className="mt-1 block font-mono text-[11px] text-brand-text-dark/60">
              {computedCron || "—"}
            </code>
          </div>

          <details
            open={advanced}
            onToggle={(e) =>
              setAdvanced((e.target as HTMLDetailsElement).open)
            }
            className="text-xs"
          >
            <summary className="cursor-pointer font-bold uppercase tracking-wider text-brand-text-dark/60">
              Advanced: raw cron
            </summary>
            <div className="mt-2">
              <input
                value={rawCron}
                onChange={(e) => setRawCron(e.target.value)}
                placeholder="z.B. */15 * * * 1-5"
                className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 font-mono text-xs"
              />
              <p className="mt-1 text-[11px] text-brand-text-dark/50">
                Wenn gefüllt, überschreibt das den Wochentag+Uhrzeit-Picker.
              </p>
            </div>
          </details>

          <button
            type="submit"
            disabled={!computedCron || create.isPending}
            className="w-full rounded-full bg-brand-blue py-2.5 text-sm font-bold uppercase tracking-wider text-brand-cream hover:bg-brand-blue-deep disabled:opacity-50"
          >
            {create.isPending ? "…" : "Schedule"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-brand-text-dark/70">
        {label}
      </div>
      {children}
    </label>
  );
}
